import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface SearchableSelectOption {
  label: string;
  value: string;
  description?: string;
}

interface SearchableSelectProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
}

export const SearchableSelect = ({
  label,
  value = '',
  onChange,
  options,
  placeholder = 'Select an option',
  emptyMessage = 'No options found.',
  searchPlaceholder = 'Search...',
  error,
  helperText,
  disabled = false,
}: SearchableSelectProps) => {
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) => {
      const haystack = [option.label, option.description || ''].join(' ').toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [options, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }

    const timerId = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 20);

    return () => window.clearTimeout(timerId);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleEscape);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="w-full">
      {label ? (
        <label htmlFor={inputId} className="form-label">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <button
          id={inputId}
          type="button"
          disabled={disabled}
          role="combobox"
          aria-controls={listboxId}
          onClick={() => {
            if (!disabled) {
              setIsOpen((current) => !current);
            }
          }}
          onKeyDown={(event) => {
            if (disabled) return;

            if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsOpen(true);
            }

            if (event.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          className={cn(
            'flex min-h-12 w-full items-center justify-between gap-3 rounded-xl border bg-white px-4 py-3 text-left shadow-sm transition duration-150',
            'focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--accent-solid))/0.14]',
            disabled
              ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
            isOpen && !disabled && 'border-[rgb(var(--accent-solid))] ring-4 ring-[rgb(var(--accent-solid))/0.12]',
            error && 'border-red-500 focus-visible:ring-red-500/15'
          )}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <div className="min-w-0">
            {selectedOption ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-950">{selectedOption.label}</p>
                {selectedOption.description ? (
                  <p className="mt-0.5 truncate text-xs text-slate-500">{selectedOption.description}</p>
                ) : null}
              </div>
            ) : (
              <span className="text-sm text-slate-400">{placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        <div
          id={listboxId}
          role="listbox"
          className={cn(
            'absolute left-0 right-0 top-full z-40 mt-2 origin-top rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 transition-all duration-150',
            isOpen ? 'pointer-events-auto scale-100 opacity-100' : 'pointer-events-none scale-[0.98] opacity-0'
          )}
        >
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[rgb(var(--accent-solid))] focus:bg-white focus:ring-4 focus:ring-[rgb(var(--accent-solid))/0.12]"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto p-2">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = option.value === value;

                return (
                  <button
                    key={option.value}
                    id={`${inputId}-option-${option.value}`}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    className={cn(
                      'flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left transition',
                      isSelected
                        ? 'bg-[rgb(var(--accent-solid))/0.10] text-[rgb(var(--accent-solid))]'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-950'
                    )}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{option.label}</p>
                      {option.description ? (
                        <p className="mt-0.5 truncate text-xs text-slate-500">{option.description}</p>
                      ) : null}
                    </div>
                    <Check className={cn('mt-0.5 h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')} />
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-5 text-center text-sm text-slate-500">{emptyMessage}</div>
            )}
          </div>
        </div>
      </div>

      {error ? <p className="mt-1.5 text-sm font-medium text-red-600">{error}</p> : null}
      {!error && helperText ? <p className="mt-1.5 text-sm text-slate-500">{helperText}</p> : null}
    </div>
  );
};
